import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Card, Descriptions, Button, Tag, Space, App, Spin, Table, Typography, Tooltip, Popconfirm } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, FileTextOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  supplierAdvanceRequestService,
  type SarInvoiceAdjustment,
  type SarLinkedPaymentVoucher,
} from '@/services/supplierAdvanceRequest.service';
import { recoveryStatusTagColor } from '@/services/supplierAdvanceRefund.service';
import { approvalService } from '@/services/approval.service';
import { EnterprisePageHeader } from '@/components/enterprise';
import { DocumentRefLink } from '@/components/DocumentRefLink';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { PrintableSupplierAdvanceRequest } from '@/components/procurement/PrintableSupplierAdvanceRequest';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { PrintableVoucher } from '@/components/sales-invoice/PrintableVoucher';
import { useSystemSettings } from '@/hooks/useSettings';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_SUPPLIER_ADVANCE_REQUEST } from '@/constants/document-entity-types';
import { headerProjectLabel } from '@/utils/projectDisplay';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';

const SupplierAdvanceRequestDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<string | null>(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [voucherPrintLoadingId, setVoucherPrintLoadingId] = useState<string | null>(null);
  const { data: departments = [] } = useDepartmentsForSelection();

  const { data: sar, isLoading } = useQuery({
    queryKey: ['supplier-advance-request', id],
    queryFn: () => supplierAdvanceRequestService.getById(id!),
    enabled: !!id,
  });

  const { data: linkedPayments = [] } = useQuery({
    queryKey: ['supplier-advance-request-payments', id],
    queryFn: () => supplierAdvanceRequestService.getLinkedPaymentVouchers(id!),
    enabled: !!id,
  });

  const { data: invoiceAdjustments = [] } = useQuery({
    queryKey: ['supplier-advance-request-adjustments', id],
    queryFn: () => supplierAdvanceRequestService.getInvoiceAdjustments(id!),
    enabled: !!id,
  });

  const { data: systemSettings } = useSystemSettings();

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setLoading(key);
    try {
      await fn();
      queryClient.invalidateQueries({ queryKey: ['supplier-advance-request', id] });
      queryClient.invalidateQueries({ queryKey: ['supplier-advance-request-payments', id] });
      message.success('Done');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Action failed');
    } finally {
      setLoading(null);
    }
  };

  const handlePrint = async () => {
    if (!id) return;
    setPrintLoading(true);
    try {
      const printData = await supplierAdvanceRequestService.getPrintData(id);
      const companySettings = systemSettings?.filter((s) => s.category === 'company') ?? [];
      const getSetting = (key: string) => companySettings.find((s) => s.setting_key === key)?.setting_value ?? '';
      const trn = getSetting('tax_registration_number');
      const companyInfo = {
        name: getSetting('company_name') || 'Company',
        address: getSetting('company_address') || '',
        phone: getSetting('company_phone') || '',
        email: getSetting('company_email') || '',
        taxNumber: trn ? `TRN: ${trn}` : '',
        bankName: getSetting('bank_name') || '',
        bankAccountNo: getSetting('bank_account_no') || getSetting('bank_account_number') || '',
        bankIban: getSetting('bank_iban') || getSetting('iban') || '',
        bankSwiftCode: getSetting('bank_swift_code') || getSetting('swift_code') || '',
      };

      let approvalSigners: Awaited<ReturnType<typeof approvalService.getPrintSigners>> = [];
      if (printData.sar.approval_instance_id) {
        try {
          approvalSigners = await approvalService.getPrintSigners('supplier_advance_request', id);
        } catch {
          approvalSigners = [];
        }
      }

      const mainContent = document.getElementById('root');
      if (mainContent) mainContent.style.display = 'none';
      const printContainer = document.createElement('div');
      printContainer.id = 'print-container';
      printContainer.style.display = 'block';
      document.body.appendChild(printContainer);
      const root = ReactDOM.createRoot(printContainer);
      const cleanup = () => {
        root.unmount();
        if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
        if (mainContent) mainContent.style.display = 'block';
        window.removeEventListener('afterprint', cleanup);
      };
      root.render(
        <PrintableSupplierAdvanceRequest
          data={printData}
          companyInfo={companyInfo}
          approvalSigners={approvalSigners}
          onClose={cleanup}
        />
      );
      window.addEventListener('afterprint', cleanup);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to load print data');
    } finally {
      setPrintLoading(false);
    }
  };

  const handlePrintVoucher = async (paymentVoucherId: string) => {
    if (!id) return;
    setVoucherPrintLoadingId(paymentVoucherId);
    try {
      const data = await supplierAdvanceRequestService.getPrintVoucherData(id, paymentVoucherId);
      const companySettings = systemSettings?.filter((s) => s.category === 'company') ?? [];
      const companyName = companySettings.find((s) => s.setting_key === 'company_name')?.setting_value || 'Company';

      const mainContent = document.getElementById('root');
      if (mainContent) mainContent.style.display = 'none';
      const printContainer = document.createElement('div');
      printContainer.id = 'print-container';
      printContainer.style.display = 'block';
      document.body.appendChild(printContainer);
      const root = ReactDOM.createRoot(printContainer);
      const cleanup = () => {
        root.unmount();
        if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
        if (mainContent) mainContent.style.display = 'block';
        window.removeEventListener('afterprint', cleanup);
      };
      root.render(
        <PrintableVoucher
          voucher={data.journal_voucher}
          title="SUPPLIER ADVANCE BOOKING"
          typeLabel="Supplier Advance Booking"
          referenceLabel="Advance request / Payment"
          companyName={companyName}
          onClose={cleanup}
        />
      );
      window.addEventListener('afterprint', cleanup);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to load accounting voucher');
    } finally {
      setVoucherPrintLoadingId(null);
    }
  };

  if (isLoading || !sar) return <Spin />;

  const canSubmit = sar.status === 'Draft';
  const canPay = ['Approved', 'Partially Paid'].includes(sar.status);
  const balance = Math.max(0, Number(sar.requested_amount) - Number(sar.paid_amount));
  const paidAmount = Number(sar.paid_amount);
  const adjustedAmount = Number(sar.adjusted_amount);
  const openLinkedPayments = linkedPayments.filter((p) => p.status !== 'Cancelled');
  const canCancelSar =
    !['Cancelled', 'Rejected'].includes(sar.status) &&
    paidAmount <= 0 &&
    adjustedAmount <= 0 &&
    openLinkedPayments.length === 0;
  const printablePayments = linkedPayments.filter((p) => p.can_print_voucher);
  const draftPayments = linkedPayments.filter((p) => p.status !== 'Posted');
  const voucherPrintDisabledReason =
    printablePayments.length === 0
      ? linkedPayments.length === 0
        ? 'Create and post a payment voucher first. SAR print shows advance booking only (Dr Advances / Cr AP).'
        : draftPayments.length > 0
          ? 'Post the payment voucher to print the advance booking journal.'
          : 'No posted payment with an advance booking journal yet.'
      : printablePayments.length > 1
        ? 'Several posted payments — use Print voucher on the row you need in the table below.'
        : null;

  const printVoucherButton = (
    <Button
      icon={<FileTextOutlined />}
      disabled={printablePayments.length !== 1}
      loading={voucherPrintLoadingId != null}
      onClick={() => {
        if (printablePayments.length === 1) {
          handlePrintVoucher(printablePayments[0].id);
        }
      }}
    >
      Print voucher
    </Button>
  );

  return (
    <div>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ marginBottom: 8 }}>
        Back
      </Button>
      <EnterprisePageHeader
        documentLabel="Supplier advance request"
        documentNumber={sar.advance_request_number}
        statusTag={<Tag>{sar.status}</Tag>}
        primaryActions={
          <Space>
            <Button icon={<PrinterOutlined />} loading={printLoading} onClick={handlePrint}>
              Print request
            </Button>
            <ReportDocumentActions
              reportKey="supplier-advance-request"
              params={{ id: sar.id }}
              documentId={sar.id}
              sendPermissions={['procurement.advance.submit', 'procurement.advance.create']}
              emailLabel="Email supplier"
            />
            <Tooltip title={voucherPrintDisabledReason ?? 'Journal voucher from posted advance payment'}>
              <span>{printVoucherButton}</span>
            </Tooltip>
          </Space>
        }
      />
      {sar.approval_instance_id && (
        <Card style={{ marginBottom: 16 }}>
          <ApprovalPanel
            entityType="supplier_advance_request"
            entityId={sar.id}
            extraInvalidateQueryKeys={[['supplier-advance-request', id]]}
          />
        </Card>
      )}
      <Card>
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="PO">
            <DocumentRefLink
              to={`/scm/procurement/purchase-orders/${sar.purchase_order_id}`}
              number={sar.purchaseOrder?.po_number}
              id={sar.purchase_order_id}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Project">
            {headerProjectLabel({
              project: sar.purchaseOrder?.project,
              project_id: sar.purchaseOrder?.project_id,
            })}
          </Descriptions.Item>
          <Descriptions.Item label="Supplier">{sar.supplier?.supplier_name ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Department">
            {sar.department_id
              ? (() => {
                  const d = departments.find((x) => x.id === sar.department_id);
                  return d ? `${d.department_code} - ${d.department_name}` : sar.department_id;
                })()
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Requested amount">{sar.currency} {Number(sar.requested_amount).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Paid">{Number(sar.paid_amount).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Adjusted">{Number(sar.adjusted_amount).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Refunded">{Number(sar.refunded_amount ?? 0).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Recovery status">
            {sar.recovery_status ? (
              <Tag color={recoveryStatusTagColor(sar.recovery_status)}>{sar.recovery_status}</Tag>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Balance to pay">{balance.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Requested date">{dayjs(sar.requested_date).format('DD MMM YYYY')}</Descriptions.Item>
          <Descriptions.Item label="Reason">{sar.reason || '—'}</Descriptions.Item>
        </Descriptions>
        <Space style={{ marginTop: 16 }} wrap>
          {canSubmit && (
            <Button loading={loading === 'submit'} onClick={() => run('submit', () => supplierAdvanceRequestService.submit(id!))}>
              Submit for approval
            </Button>
          )}
          {canPay && balance > 0 && (
            <Button
              type="primary"
              onClick={() => navigate(`/fin/payment-vouchers/new?advanceRequestId=${id}`)}
            >
              Create payment voucher
            </Button>
          )}
          {canCancelSar && (
            <Popconfirm
              title="Cancel this supplier advance request?"
              description="You can create a new request on the PO after cancellation (if advance capacity allows)."
              onConfirm={() => run('cancel', () => supplierAdvanceRequestService.cancel(id!))}
              okText="Cancel request"
              cancelText="Keep"
            >
              <Button danger loading={loading === 'cancel'}>
                Cancel request
              </Button>
            </Popconfirm>
          )}
          <Button onClick={() => navigate(`/scm/procurement/purchase-orders/${sar.purchase_order_id}`)}>View PO</Button>
          {Number(sar.refunded_amount ?? 0) > 0 || sar.recovery_status ? (
            <Button
              onClick={() =>
                navigate(`/finance/ap/supplier-advance-refunds?supplier_advance_request_id=${id}`)
              }
            >
              Refund history
            </Button>
          ) : null}
        </Space>
      </Card>

      <Card title="Payment vouchers & accounting" style={{ marginTop: 16 }}>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          <strong>Print request</strong> — advance request document for approval.{' '}
          <strong>Print voucher</strong> — advance booking only (Dr Advances / Cr AP). Bank payment
          (Dr AP / Cr bank) prints from the Payment Voucher.
        </Typography.Paragraph>
        {linkedPayments.length === 0 ? (
          <Typography.Text type="secondary">
            No payment vouchers yet. Use &quot;Create payment voucher&quot;, then post it from Finance to enable Print voucher.
          </Typography.Text>
        ) : (
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={linkedPayments}
            columns={[
              { title: 'Payment voucher', dataIndex: 'voucher_number' },
              {
                title: 'Date',
                dataIndex: 'voucher_date',
                render: (v: string) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
              },
              {
                title: 'Amount',
                key: 'amount',
                align: 'right' as const,
                render: (_: unknown, r: SarLinkedPaymentVoucher) =>
                  `${r.currency} ${Number(r.amount).toFixed(2)}`,
              },
              { title: 'Mode', dataIndex: 'payment_mode' },
              {
                title: 'Status',
                dataIndex: 'status',
                render: (st: string) => <Tag>{st}</Tag>,
              },
              {
                title: '',
                key: 'actions',
                render: (_: unknown, r: SarLinkedPaymentVoucher) => (
                  <Space>
                    <Button type="link" size="small" onClick={() => navigate(`/fin/payment-vouchers/${r.id}`)}>
                      Open
                    </Button>
                    {r.can_print_voucher && (
                      <Button
                        type="link"
                        size="small"
                        icon={<FileTextOutlined />}
                        loading={voucherPrintLoadingId === r.id}
                        onClick={() => handlePrintVoucher(r.id)}
                      >
                        Print voucher
                      </Button>
                    )}
                    {r.status !== 'Posted' && (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Post payment to print GL
                      </Typography.Text>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Card title="Applied to supplier invoices" style={{ marginTop: 16 }}>
        {invoiceAdjustments.length === 0 ? (
          <Typography.Text type="secondary">No advance has been applied to supplier invoices yet.</Typography.Text>
        ) : (
          <Table<SarInvoiceAdjustment>
            size="small"
            rowKey={(r) => `${r.payment_voucher_id}-${r.supplier_invoice_id}-${r.adjustment_date}`}
            pagination={false}
            dataSource={invoiceAdjustments}
            columns={[
              { title: 'Payment voucher', dataIndex: 'voucher_number' },
              { title: 'Invoice', dataIndex: 'invoice_number' },
              {
                title: 'Supplier inv. #',
                dataIndex: 'supplier_invoice_number',
                render: (v: string) => v ?? '—',
              },
              {
                title: 'Amount',
                key: 'amount',
                align: 'right' as const,
                render: (_: unknown, r: SarInvoiceAdjustment) =>
                  `${r.currency} ${Number(r.allocated_amount).toFixed(2)}`,
              },
              {
                title: 'Adjustment date',
                dataIndex: 'adjustment_date',
                render: (v: string) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
              },
            ]}
          />
        )}
      </Card>

      <ProcurementAttachmentsCard
        entityType={DOCUMENT_ENTITY_TYPE_SUPPLIER_ADVANCE_REQUEST}
        entityId={sar.id}
        title="Attachments"
        readOnly={sar.status === 'Cancelled' || sar.status === 'Rejected'}
        queryKeyPrefix={['documents', 'supplier-advance-request']}
      />
    </div>
  );
};

export default SupplierAdvanceRequestDetailPage;
